import Head from 'next/head';
import { useRouter } from 'next/router';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  article?: {
    publishedTime: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
  product?: {
    price?: number;
    currency?: string;
    availability?: string;
    brand?: string;
    category?: string;
  };
  keywords?: string[];
  noIndex?: boolean;
  /**
   * Override googlebot directive, e.g., to allow Google Merchant Center
   * to crawl verified products even if temporarily out of stock
   */
  googleBot?: string;
}

const SEO = ({
  title,
  description,
  canonicalUrl,
  ogImage = '/images/og-default.jpg',
  ogType = 'website',
  article,
  product,
  keywords = [],
  noIndex = false,
  googleBot,
}: SEOProps) => {
  const router = useRouter();

  // Paginated pages (page 2+) should not be indexed — only the base URL
  const pageParam = router.query.page;
  const isPaginated = pageParam && parseInt(pageParam as string, 10) > 1;

  // Clean title of any ID numbers or unnecessary information
  const cleanTitle = title.replace(/\s*\[.*?\]\s*|\s*\(.*?\)\s*/g, ' ').trim();

  // The <title> tag always appends TITLE_SUFFIX below. Budget the truncation so
  // the FULL rendered title (visible title + suffix) stays within Google's
  // length, and cut on a WORD boundary so we never slice a word in half.
  // (The old code truncated the bare title at 60 chars BEFORE the suffix was
  // added, so real titles rendered ~72 chars and Google cut them mid-phrase,
  // e.g. "…Specs, Reviews, Use... | GearFocus".)
  const TITLE_SUFFIX = ' | GearFocus';
  const TITLE_MAX_LENGTH = 70; // full rendered <title>, suffix included
  // Reserve one char for the '…' so a truncated title never exceeds the budget.
  const titleBudget = TITLE_MAX_LENGTH - TITLE_SUFFIX.length - 1;
  const finalTitle =
    cleanTitle.length > titleBudget
      ? cleanTitle.slice(0, titleBudget).replace(/\s+\S*$/, '').trimEnd() + '…'
      : cleanTitle;

  // Truncate description to Google's recommended length (150-160 characters)
  const finalDescription = description.length > 160
    ? description.substring(0, 157) + '...'
    : description;
    
  // Generate the canonical URL
  const baseUrl = 'https://gearfocus.com';
  const currentPath = router.asPath.split('?')[0]; // Remove query parameters
  const pageUrl = canonicalUrl || `${baseUrl}${currentPath}`;
  
  return (
    <Head>
      <title>{`${finalTitle}${TITLE_SUFFIX}`}</title>
      <meta name="description" content={finalDescription} />
      
      {/* SEO meta tags */}
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Canonical link */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content="GearFocus Marketplace" />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />
      
      {/* Article specific tags */}
      {ogType === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta property="article:tag" content={tag} key={`tag-${index}`} />
          ))}
        </>
      )}
      
      {/* Product specific tags */}
      {ogType === 'product' && product && (
        <>
          {product.price && (
            <meta property="product:price:amount" content={product.price.toString()} />
          )}
          {product.currency && (
            <meta property="product:price:currency" content={product.currency} />
          )}
          {product.availability && (
            <meta property="product:availability" content={product.availability} />
          )}
          {product.brand && (
            <meta property="product:brand" content={product.brand} />
          )}
          {product.category && (
            <meta property="product:category" content={product.category} />
          )}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />
      
      {/* Mobile-specific meta tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      
      {/* Robots directive - controls general search engine behavior */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : isPaginated ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Googlebot override - allows Google Merchant Center to crawl verified products
          even when temporarily out of stock (important for product feed quality) */}
      {googleBot && (
        <meta name="googlebot" content={googleBot} />
      )}
    </Head>
  );
};

export default SEO; 